// ---------------------------------------------------------------------------
// Pontas CONTÍNUAS da lateral (Preço x Força): corte por X no shader.
//
// A lateral tem o X achatado pelo grupo (scale=[0,1,1]), então não pode usar planos de corte em X como o chão.
// O recorte por setDrawRange é por índice: cada vela que entra ou sai pela ponta acrescenta ou tira um segmento
// INTEIRO de uma vez (o "pedaço que aparece/some" medido pela sonda). Aqui o shader da linha recebe o X ORIGINAL do
// vértice (o atributo de posição, antes da matriz do grupo que achata o X), interpola por fragmento e descarta o que
// fica fora da janela [lo, hi]. A linha termina exatamente na borda e desliza junto com ela, como no chão.
//
// - Não altera dados, buffers nem o FastLine: só o shader do material DESTA linha (onBeforeCompile), com chave de
//   programa própria; chão e filamento seguem com o programa padrão.
// - O setDrawRange do recorte continua igual (só reduz o trabalho de desenho); o descarte apara o segmento da ponta.
// - Por quadro: só escreve dois números em uniforms já existentes (sem alocação).
// - Se o FastLine recriar o material (ex.: troca de opacidade), o novo material é ajustado no quadro seguinte.
// ---------------------------------------------------------------------------

export const SIDE_X_CLIP_KEY = 'flowaes-lateral-corte-x';
const keyFn = () => SIDE_X_CLIP_KEY;

/** Estado por linha: uniforms da janela e o material já ajustado. */
export interface SideXClip {
  lo: { value: number }; hi: { value: number };
  mat: any;
  /** shader do último programa compilado recebeu o corte (false = âncoras não encontradas; linha fica como antes) */
  injected: boolean;
  onBC: (shader: any) => void;
}

const VS_ANCHOR_DECL = '#include <common>';
const VS_ANCHOR_MAIN = '#include <begin_vertex>';
const FS_ANCHOR_DECL = '#include <common>';
const FS_ANCHOR_MAIN = '#include <clipping_planes_fragment>';

/** Injeta o corte por X no código do shader (puro: testável fora do navegador). Devolve false sem mexer se faltar âncora. */
export function injectSideXClip(shader: { vertexShader: string; fragmentShader: string; uniforms: any }, lo: { value: number }, hi: { value: number }): boolean {
  const vs = shader.vertexShader, fs = shader.fragmentShader;
  if (!vs.includes(VS_ANCHOR_DECL) || !vs.includes(VS_ANCHOR_MAIN) || !fs.includes(FS_ANCHOR_DECL) || !fs.includes(FS_ANCHOR_MAIN)) return false;
  shader.uniforms.uSideXLo = lo;
  shader.uniforms.uSideXHi = hi;
  shader.vertexShader = vs
    .replace(VS_ANCHOR_DECL, `${VS_ANCHOR_DECL}\nvarying float vSideX;`)
    .replace(VS_ANCHOR_MAIN, `${VS_ANCHOR_MAIN}\n\tvSideX = position.x;`);   // X do vértice antes do grupo achatar
  shader.fragmentShader = fs
    .replace(FS_ANCHOR_DECL, `${FS_ANCHOR_DECL}\nuniform float uSideXLo;\nuniform float uSideXHi;\nvarying float vSideX;`)
    .replace(FS_ANCHOR_MAIN, `${FS_ANCHOR_MAIN}\n\tif ( vSideX < uSideXLo || vSideX > uSideXHi ) discard;`);
  return true;
}

export function newSideXClip(): SideXClip {
  const st: SideXClip = {
    lo: { value: -1e30 }, hi: { value: 1e30 }, mat: null, injected: false,
    onBC: (shader: any) => { st.injected = injectSideXClip(shader, st.lo, st.hi); },
  };
  return st;
}

/**
 * Por quadro: ajusta o material da linha (uma vez por material) e escreve a janela [win[off], win[off+1]].
 * A janela vem num Float64Array para não criar números em caixa a cada quadro.
 */
export function applySideXClip(line: any, st: SideXClip, win: Float64Array, off: number): void {
  if (!line) return;
  const mat = line.material;
  if (mat !== st.mat) {
    st.mat = mat;
    if (mat) {
      mat.onBeforeCompile = st.onBC;
      mat.customProgramCacheKey = keyFn;
      mat.userData.sideXClip = st;           // a sonda (?dbg=1) lê daqui a janela em uso
      mat.needsUpdate = true;
    }
  }
  st.lo.value = win[off];
  st.hi.value = win[off + 1];
}
